import { Link } from "wouter";
import BasketBoard, { BasketMiniList } from "../components/BasketBoard";
import HowItWorks from "../components/HowItWorks";
import FaqSection from "../components/FaqSection";
import NextPayoutCountdown from "../components/NextPayoutCountdown";
import LotterySection from "../components/LotterySection";
import SpendStatusBanner from "../components/SpendStatusBanner";
import {
  INDEX_SYMBOL,
  STONKFUN_TOKEN,
  claimableSol,
  fmtSol,
  fmtTime,
  spendWaitingOnFees,
} from "../lib/data";

export default function Home({ data }) {
  const basket = data?.basket;
  const entries = basket?.entries || [];
  const topWeight = entries[0] ? (Number(entries[0].weight) * 100).toFixed(1) : "—";
  const feesSol = claimableSol(data?.fees);
  const spend = data?.spend;
  const metrics = data?.metrics;
  const schedule = data?.schedule || { payoutIntervalMin: 10, minDistributeSol: 0.02 };

  return (
    <>
      <section className="home-hero">
        <div className="hero-wash" aria-hidden />
        <div className="hero-grid">
          <div className="hero-copy">
            <p className="hero-eyebrow">
              <i className="brand-diamond" aria-hidden /> Rotating StonkFun index
            </p>
            <h1 className="hero-title">
              One token,
              <br />
              <em>unlimited possibilities.</em>
            </h1>
            <p className="hero-sub">
              {`Hold $${INDEX_SYMBOL}. Creator fees buy the live board and pay out to holders every ${schedule.payoutIntervalMin || 10} minutes.`}
            </p>
            <div className="cta-row">
              <a className="btn btn-lime" href={STONKFUN_TOKEN} target="_blank" rel="noreferrer">
                {`Buy $${INDEX_SYMBOL}`}
              </a>
              <Link href="/portfolio" className="btn btn-ghost-dark">
                Portfolio
              </Link>
            </div>
          </div>

          <aside className="board-panel">
            <div className="board-panel__head">
              <div>
                <span className="eyebrow-label">Live board</span>
                <strong className="board-version">v{basket?.version ?? "—"}</strong>
              </div>
              <div className="board-stats">
                <div>
                  <span>Names</span>
                  <b>{basket?.size ?? entries.length}</b>
                </div>
                <div>
                  <span>Board</span>
                  <b>{basket?.minutesUntilRefresh ?? "—"}m</b>
                </div>
                <div>
                  <span>Top</span>
                  <b>
                    {entries[0]?.symbol || "—"} {topWeight}%
                  </b>
                </div>
              </div>
            </div>
            <BasketMiniList entries={entries} />
            <div className="board-panel__foot">
              <span>Updated {fmtTime(basket?.createdAt)}</span>
              <NextPayoutCountdown schedule={schedule} compact />
            </div>
          </aside>
        </div>
      </section>

      <section className="payout-strip">
        <div className="shell">
          <NextPayoutCountdown schedule={schedule} spend={spend} />
          <SpendStatusBanner data={data} />
        </div>
      </section>

      <HowItWorks />

      <LotterySection data={data} />

      <section className="app-section">
        <div className="shell">
          <div className="section-intro">
            <p className="section-kicker">
              <i className="brand-diamond" aria-hidden /> Snapshot
            </p>
            <h2 className="section-title">Protocol metrics</h2>
            <p className="section-lead left">
              Fee treasury, board state, and payout cadence from the live worker.
            </p>
          </div>

          <div className="metric-rail">
            <div className="metric">
              <div className="label">Claimable</div>
              <strong>{feesSol != null ? `${fmtSol(feesSol)}` : "—"}</strong>
              <p className="metric-note">
                {spendWaitingOnFees(spend)
                  ? "Waiting on more volume"
                  : "SOL ready to cycle"}
              </p>
            </div>
            <div className="metric">
              <div className="label">Payouts</div>
              <strong>every {schedule.payoutIntervalMin || 10}m</strong>
              <p className="metric-note">
                UTC · ≥ {schedule.minDistributeSol ?? 0.02} SOL liquid
              </p>
            </div>
            <div className="metric">
              <div className="label">Basket</div>
              <strong>v{basket?.version ?? "—"}</strong>
              <p className="metric-note">
                {basket?.size ?? entries.length} names · refresh {basket?.refreshMin ?? 60}m
              </p>
            </div>
            <div className="metric">
              <div className="label">Cycles</div>
              <strong>{metrics?.cycleCount ?? spend?.cycleCount ?? 0}</strong>
              <p className="metric-note">Spend runs archived</p>
            </div>
            <Link href="/cycles" className="metric-link">
              Cycles →
            </Link>
          </div>
        </div>
      </section>

      <section className="app-section flush-top">
        <div className="shell">
          <div className="section-intro">
            <p className="section-kicker">
              <i className="brand-diamond" aria-hidden /> Basket
            </p>
            <h2 className="section-title">Weighted set</h2>
            <p className="section-lead left">
              Fee SOL buys each name by score weight. Liquidity comes from
              StonkFun markets.
            </p>
          </div>
          <BasketBoard basket={basket} payoutTotals={data?.payoutTotals} />
          <div className="section-actions section-actions--row">
            <Link href="/portfolio" className="btn btn-lime">
              Portfolio
            </Link>
            <Link href="/yield" className="btn btn-ghost">
              Yield calculator
            </Link>
            <Link href="/swap" className="btn btn-ghost">
              Swap
            </Link>
          </div>
        </div>
      </section>

      <FaqSection data={data} />

      <section className="cta-band">
        <div className="shell cta-band__inner">
          <div>
            <h2>{`Hold $${INDEX_SYMBOL}. Get paid in the board.`}</h2>
            <p>
              {spendWaitingOnFees(spend)
                ? `Payout clock is live — currently waiting on ≥ ${schedule.minDistributeSol ?? 0.02} SOL fees before the next buy.`
                : `Payouts attempt every ${schedule.payoutIntervalMin || 10} minutes when fees are ready.`}
            </p>
          </div>
          <div className="cta-row">
            <Link href="/swap" className="btn btn-lime">
              {`Buy $${INDEX_SYMBOL}`}
            </Link>
            <Link href="/portfolio" className="btn btn-ghost-dark">
              Track portfolio
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
